// Quick-start templates for the Holiday Schedule.
//
// A template is a one-click bulk edit of one category: `apply(holiday, ctx)` runs
// over every holiday in that category and returns the holiday's next state. ctx
// carries party metadata (names, pronouns) for the gender-aware rules.
//
// `reversible: true` means the template has a meaningful A/B mirror, which is what
// the per-category "Swap parents" button re-applies.
//
// These decide what a separation agreement says about who has the children on a
// given day, so read each `apply` against the description above it before editing.

// Chronological order slots for the alt-stat / alt-long-weekends templates.
// Province-unique holidays that occupy the same seasonal slot share the same
// index so alternation parity stays consistent regardless of jurisdiction.
// Easter Monday shares Good Friday's slot (Easter weekend cohesion — same
// parent for the whole weekend, not alternating within it).
const STAT_CHRONO_ORDER = {
  // Slot 0 — February / early March (mid-winter observance)
  'family-day':                     0,  // BC, AB, NB, ON, SK, YT
  'louis-riel-day':                 0,  // MB
  'islander-day':                   0,  // PE
  'heritage-day-ns':                0,  // NS
  'st-patricks-day':                0,  // NL (nearest Mon to Mar 17)
  // Slot 1 — Easter weekend + April
  'good-friday':                    1,
  'easter-monday':                  1,
  'st-georges-day':                 1,  // NL (nearest Mon to Apr 23)
  // Slot 2 — Late May & June
  'victoria-day':                   2,
  'national-indigenous-peoples-day': 2, // NT, YT (Jun 21)
  'discovery-day-nl':               2,  // NL (nearest Mon to Jun 24)
  // Slot 3 — July
  'canada-day':                     3,
  'memorial-day':                   3,  // NL (Jul 1, alongside Canada Day)
  'nunavut-day':                    3,  // NU (Jul 9)
  'orangemans-day':                 3,  // NL (nearest Mon to Jul 12)
  // Slot 4 — August (1st Mon usually; PEI Gold Cup is 3rd Fri; YT Discovery is 3rd Mon)
  'bc-day':                         4,  // BC
  'heritage-day':                   4,  // AB (Aug — distinct from NS's Feb Heritage Day)
  'terry-fox-day':                  4,  // MB
  'new-brunswick-day':              4,  // NB
  'saskatchewan-day':               4,  // SK
  'civic-holiday':                  4,  // ON, NS, NT, NU
  'gold-cup-parade-day':            4,  // PE (3rd Fri of August)
  'discovery-day':                  4,  // YT (3rd Mon of August — distinct from NL's June Discovery Day)
  // Slot 5 — Labour Day
  'labour-day':                     5,
  // Slot 6 — Sept 30
  'truth-reconciliation':           6,
  // Slot 7 — Thanksgiving
  'thanksgiving':                   7,
  // Slot 8 — Remembrance Day
  'remembrance-day':                8,
}

// Templates that apply universally to any category: bulk-set every row in
// the section to "no-change" (keep regular parenting time) or "suspend-flex"
// (decide each occurrence by agreement). Spread into every category's array
// below so they're always available regardless of section.
const UNIVERSAL_TEMPLATES = [
  {
    key: 'no-change-all',
    title: "Don't change the schedule",
    description: 'Keep the regular parenting schedule on every day in this section.',
    apply: (h) => ({ ...h, rule: { kind: 'no-change' } }),
  },
  {
    key: 'decide-later-all',
    title: 'Take a flexible approach',
    description: 'Pause the regular schedule on every day in this section — the parties decide together with reasonable notice.',
    apply: (h) => ({ ...h, rule: { kind: 'suspend-flex' } }),
  },
]

// Quick-start templates, scoped per category. Each template's `apply(h, ctx)`
// runs only on holidays in its own category — categories don't influence each other.
export const TEMPLATES_BY_CATEGORY = {
  'holiday': [
    {
      key: 'alt-stat',
      title: 'Alternate statutory holidays evenly',
      description: "Within each year the holidays alternate parent-by-parent in chronological order. Each holiday then flips parents the next year, so the load stays balanced across both axes.",
      reversible: true,
      apply: (h) => {
        const idx = STAT_CHRONO_ORDER[h.id]
        if (idx === undefined) return h // custom or unrecognized — leave unset
        const evenParent = idx % 2 === 0 ? 'A' : 'B'
        return { ...h, rule: { kind: 'alternating-by-year', evenParent } }
      },
    },
    {
      key: 'tie-long-weekends',
      title: 'Tie long-weekend holidays to the weekend parent',
      description: "Long-weekend-eligible holidays absorb into whoever has the regular weekend. Mid-week holidays stay unset.",
      apply: (h) => h.longWeekendType ? { ...h, rule: { kind: 'tie-to-weekend' } } : h,
    },
    {
      key: 'alt-long-weekends',
      title: 'Alternate the long weekend each year',
      description: "Long-weekend-eligible holidays apply to the full Fri to Sun (or Sat to Mon) block. Within each year the eligible holidays alternate parent-by-parent in chronological order, and each one flips parents the next year. Mid-week holidays stay unset.",
      reversible: true,
      apply: (h) => {
        if (!h.longWeekendType) return h // mid-week, leave unset
        const idx = STAT_CHRONO_ORDER[h.id]
        if (idx === undefined) return h
        const evenParent = idx % 2 === 0 ? 'A' : 'B'
        return { ...h, rule: { kind: 'alternating-by-year', evenParent, scope: 'long-weekend' } }
      },
    },
    ...UNIVERSAL_TEMPLATES,
  ],

  'special-day': [

    {
      key: 'parent-days',
      title: "Each parent's day with that parent",
      description: "Mother's Day with the parent who uses she/her, Father's Day with the parent who uses he/him, each parent's birthday with themselves, children's birthday shared. Days without a clear pronoun match stay unset.",
      apply: (h, ctx) => {
        if (h.id === 'mothers-day')   return matchByPronoun(h, ctx, 'she/her')
        if (h.id === 'fathers-day')   return matchByPronoun(h, ctx, 'he/him')
        if (h.id === 'party-a-birthday')   return { ...h, rule: { kind: 'fixed', parent: 'A' } }
        if (h.id === 'party-b-birthday')   return { ...h, rule: { kind: 'fixed', parent: 'B' } }
        if (h.id === 'childrens-birthday') return { ...h, rule: { kind: 'share' } }
        return h
      },
    },
    {
      key: 'alt-special',
      title: 'Alternate all special days annually',
      description: 'Every special day alternates by year. A simple starting point if no day stands out as fixed.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'alternating-by-year', evenParent: 'A' } }),
    },
    ...UNIVERSAL_TEMPLATES,
  ],

  'school-break': [
    {
      key: 'alt-breaks',
      title: 'Alternate breaks annually',
      description: 'Each break (Spring, Summer, Winter) goes to one parent for the entire break, alternating yearly.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'alternating-by-year', evenParent: 'A' } }),
    },
    {
      key: 'split-each-break',
      title: 'Split each break in half',
      description: 'First half of each break with one parent, second half with the other.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'split-period', firstHalfParent: 'A' } }),
    },
    {
      key: 'week-on-off-breaks',
      title: 'Week on/off rotation through each break',
      description: 'Parents rotate parenting time on a one-week-on / one-week-off basis through each break.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'week-on-off', startParent: 'A' } }),
    },
    ...UNIVERSAL_TEMPLATES,
  ],

  'christmas': [
    {
      key: 'eve-day-split-alt',
      title: "Eve/Day split, alternating each year",
      description: "Christmas Eve/Day and New Year's Eve/Day each split at noon. Each year the parents swap which 24-hour block they get.",
      reversible: true,
      apply: (h) => {
        if (h.id === 'christmas-eve')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'A', splitTime: '12:00' } }
        if (h.id === 'christmas-day')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'B', splitTime: '12:00' } }
        if (h.id === 'new-years-eve')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'A', splitTime: '12:00' } }
        if (h.id === 'new-years-day')  return { ...h, rule: { kind: 'paired-day-split', evenParent: 'B', splitTime: '12:00' } }
        return h
      },
    },
    {
      key: 'split-each-at-noon',
      title: 'Split each day at noon',
      description: 'Each of the four days is split at noon, with the morning going to one parent and the afternoon to the other.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'split-day', firstHalfParent: 'A', transitionTime: '12:00' } }),
    },
    {
      key: 'alt-each-day',
      title: 'Alternate each day annually',
      description: 'Christmas Eve, Christmas Day, NY Eve, and NY Day each alternate by year independently.',
      reversible: true,
      apply: (h) => ({ ...h, rule: { kind: 'alternating-by-year', evenParent: 'A' } }),
    },
    ...UNIVERSAL_TEMPLATES,
  ],
}

function matchByPronoun(h, ctx, target) {
  const aMatches = ctx.partyAPronouns === target
  const bMatches = ctx.partyBPronouns === target
  if (aMatches && !bMatches) return { ...h, rule: { kind: 'fixed', parent: 'A' } }
  if (bMatches && !aMatches) return { ...h, rule: { kind: 'fixed', parent: 'B' } }
  return h // both match (same-gender couple) or neither match — leave unset
}
